import React, { useMemo, useReducer } from 'react';
import { observer } from 'mobx-react';
import { Checkbox } from 'ksnm-common-ui/lib/Checkbox';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';
import type { DataSourceRow } from 'ksnm-common-ui/lib/Table2/Table.interface';
import type { Filter as TableFilter } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useToggleFullSize } from '@src/utils/hooks/useToggleFullSize';
import { constantStore } from '@stores/constantStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';
import { logsStore } from '@stores/logsStore';
import { formatDate } from '@utils';

import { getColumns } from '../columns';
import { Filter } from '../Filter';
import type { IProcess } from '../Logs';

import styles from '../Logs.module.scss';

const { TABLE_NAMES } = constantStore;

interface ITable {
  fullSize: boolean;
  isProcessSelected: boolean;
  selectedProcess: string[];
  setProcess: React.Dispatch<React.SetStateAction<IProcess | null>>;
  toggleFullSize: React.DispatchWithoutAction;
}

const ActionButton = ({ fullSize, toggleFullSize }) => {
  const onToggleFullSize = useToggleFullSize({
    tableName: TABLE_NAMES.LOGS,
    columns: getColumns(),
    fullSize,
    toggle: toggleFullSize,
    withCheckbox: false,
  });

  return (
    <IconButton
      title={fullSize ? 'Уменьшить' : 'Увеличить'}
      onClick={onToggleFullSize}
      iconName={fullSize ? 'fullScreenOpen' : 'fullScreenClose'}
    />
  );
};

export const Table = observer(
  ({ fullSize, isProcessSelected, selectedProcess, setProcess, toggleFullSize }: ITable): React.ReactElement | null => {
    const { filterValues } = logsStore;
    const { guid = '', dateTo, dateFrom } = filterValues;

    const [isOnlyErrors, setIsOnlyErrors] = useReducer((value) => !value, false);

    const hardFilter: TableFilter[][] = useMemo(() => {
      const initFilter: TableFilter[] = [
        { type: FILTER.EQ, name: 'etl_process_name', values: selectedProcess, filterIsInverted: false },
      ];

      if (isOnlyErrors) initFilter.push({ type: FILTER.EQ, name: 'type_res', values: ['E'], filterIsInverted: false });

      if (dateTo && dateFrom) {
        const formatDateFrom = formatDate({ date: dateFrom, outputFormat: 'YYYY-MM-DD 00:00:00.000' });
        const formatDateTo = formatDate({ date: dateTo, outputFormat: 'YYYY-MM-DD 23:59:59.999' });

        if (formatDateFrom && formatDateTo)
          initFilter.push({
            name: 'start_date',
            type: FILTER.RNG,
            values: [formatDateFrom, formatDateTo],
            filterIsInverted: false,
          });
      }

      if (guid.length) {
        initFilter.push({ type: FILTER.MASK, name: 'guid', values: [guid], filterIsInverted: false });
      }

      return [initFilter];
    }, [dateFrom, dateTo, guid, isOnlyErrors, selectedProcess]);

    const onRowClick = (row: DataSourceRow) => {
      if (row.guid && typeof row.guid === 'string') {
        setProcess({
          guid: row.guid,
          reportPackageCode: row.report_package_code_cm ?? '',
          jsonObjects: row.json_objects,
        } as IProcess);
      }
    };

    const title = useMemo(
      () => (
        <Checkbox checked={isOnlyErrors} onChange={setIsOnlyErrors} title="Отображать только ошибки">
          Отображать только ошибки
        </Checkbox>
      ),
      [isOnlyErrors],
    );

    const tableKey = `${selectedProcess?.[0]}-isOnlyErrors-${isOnlyErrors}-${fullSize}`;

    const columns = useMemo(() => {
      const { etlTypes } = logsStore;

      const isExportFNS = etlTypes.find(
        (item) => item.processName === selectedProcess.at(0) && item.etlGroup === 'Экспорт данных в ИС ПЛД',
      );

      return getColumns(!!isExportFNS);
    }, [selectedProcess]);

    if (!selectedProcess.length || !isProcessSelected) return null;

    return (
      <div className={styles.tableWithSearch}>
        <Filter />
        <KSNMTable
          tableTitle={title}
          tableName={TABLE_NAMES.LOGS}
          columns={columns}
          resource="log.v_etl_process_tc"
          externalFetch={ksnmTableStore.externalFetch}
          externalFetchXLSX={getExcelWithAudit(`Экспорт лога. ${selectedProcess}.`)}
          hardFilter={hardFilter}
          cellHeight={42}
          key={tableKey}
          onRowClick={onRowClick}
          keyField="guid"
          extendTableButtons={<ActionButton fullSize={fullSize} toggleFullSize={toggleFullSize} />}
        />
      </div>
    );
  },
);
